import { useMemo, useState } from "react";
import { DisputedBracket } from "core/models/Bracket";
import { Input, SelectPicker } from "rsuite";

interface BracketAllResultsTableProps {
  brackets: any;
  handleResultClick: (seed: DisputedBracket) => void;
}

type BracketRegionKey = "upper" | "lower" | "final" | "thirdPlace" | "roundRobin";

const REGION_LABEL_MAP: Record<BracketRegionKey, string> = {
  upper: "Upper",
  lower: "Lower",
  final: "Final",
  thirdPlace: "Third Place",
  roundRobin: "Round Robin",
};

export default function BracketAllResultsTable({
  brackets,
  handleResultClick,
}: BracketAllResultsTableProps) {
  const [selectedRegion, setSelectedRegion] = useState<string>("all");
  const [selectedRound, setSelectedRound] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [searchText, setSearchText] = useState("");

  const allRows = useMemo(() => {
    if (!brackets) return [];

    const regions: BracketRegionKey[] = [
      "upper",
      "lower",
      "final",
      "thirdPlace",
      "roundRobin",
    ];

    return regions.flatMap((regionKey) => {
      const rounds = (brackets?.[regionKey] || []) as any[];

      return rounds.flatMap((round: any) => {
        const seeds = round?.seeds || [];
        return seeds.map((seed: any) => ({
          ...seed,
          __regionKey: regionKey,
          __regionLabel: REGION_LABEL_MAP[regionKey],
          __roundName: seed?.roundName || round?.title || `Round ${seed?.roundSequence || "-"}`,
          __teamsText: (seed?.teams || [])
            .filter((team: any) => team?.name)
            .map((team: any) => team.name)
            .join(" vs "),
        }));
      });
    });
  }, [brackets]);

  const roundOptions = useMemo(() => {
    const roundSet = new Set<string>();
    allRows.forEach((row) => {
      if (row.__roundName) {
        roundSet.add(row.__roundName);
      }
    });
    return Array.from(roundSet);
  }, [allRows]);

  const filteredRows = useMemo(() => {
    return allRows.filter((row) => {
      if (selectedRegion !== "all" && row.__regionKey !== selectedRegion) {
        return false;
      }
      if (selectedRound !== "all" && row.__roundName !== selectedRound) {
        return false;
      }

      const approvalStatus = (row?.approvalStatus || "").toLowerCase();
      if (selectedStatus !== "all" && approvalStatus !== selectedStatus) {
        return false;
      }

      const normalizedSearch = searchText.trim().toLowerCase();
      if (!normalizedSearch) return true;

      const searchable = [
        row?.bracketId,
        row?.matchNumber,
        row?.matchSequence,
        row?.__roundName,
        row?.__regionLabel,
        row?.__teamsText,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchable.includes(normalizedSearch);
    });
  }, [allRows, selectedRegion, selectedRound, selectedStatus, searchText]);

  if (!allRows.length) {
    return <div className="no-data">No bracket result available</div>;
  }

  return (
    <>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, minmax(140px, 1fr))",
          gap: "1rem",
          marginTop: "1.6rem",
        }}
      >
        <SelectPicker
          value={selectedRegion}
          cleanable={false}
          searchable={false}
          style={{ width: "100%" }}
          onChange={(value) => setSelectedRegion(String(value || "all"))}
          data={[
            { label: "All Brackets", value: "all" },
            ...Object.entries(REGION_LABEL_MAP).map(([key, label]) => ({
              label,
              value: key,
            })),
          ]}
        />

        <SelectPicker
          value={selectedRound}
          cleanable={false}
          searchable
          style={{ width: "100%" }}
          onChange={(value) => setSelectedRound(String(value || "all"))}
          data={[
            { label: "All Rounds", value: "all" },
            ...roundOptions.map((round) => ({
              label: round,
              value: round,
            })),
          ]}
        />

        <SelectPicker
          value={selectedStatus}
          cleanable={false}
          searchable={false}
          style={{ width: "100%" }}
          onChange={(value) => setSelectedStatus(String(value || "all"))}
          data={[
            { label: "All Status", value: "all" },
            { label: "Open", value: "open" },
            { label: "Pending", value: "pending" },
            { label: "Disputed", value: "disputed" },
            { label: "Approved", value: "approved" },
            { label: "Resolved", value: "resolved" },
            { label: "Auto Resolved", value: "auto resolved" },
          ]}
        />

        <Input
          value={searchText}
          onChange={(value) => setSearchText(value)}
          placeholder="Search team / match / bracket"
        />
      </div>

      {filteredRows.length === 0 ? (
        <div className="no-data">No result found for selected filters</div>
      ) : (
        <div className="bracket-dispute-table-wrapper">
          <div className="grid-row">
            <span className="heading">Bracket</span>
            <span className="heading">Round</span>
            <span className="heading">Match No</span>
            <span className="heading">Teams</span>
            <span className="heading">Status</span>
            <span className="heading">Action</span>
          </div>

          {filteredRows.map((row, index) => (
            <div className="grid-row bracket-head" key={`${row?.id}-${index}`}>
              <span className="heading">{row.__regionLabel}</span>
              <span className="heading">{row.__roundName}</span>
              <span className="heading">{row?.matchNumber || row?.matchSequence || "-"}</span>
              <span className="heading">{row.__teamsText || "-"}</span>
              <span className="heading">
                <span
                  className={`status-badge ${String(
                    row?.approvalStatus || "-"
                  )
                    .toLowerCase()
                    .replace(/\s+/g, "-")}`}
                >
                  {row?.approvalStatus || "-"}
                </span>
              </span>
              <span
                className="heading"
                onClick={() => {
                  const bracketDocId = row?.id || row?._id;
                  if (!bracketDocId) return;
                  handleResultClick({
                    ...row,
                    id: bracketDocId,
                  });
                }}
              >
                <span
                  className="view"
                  style={{
                    opacity: row?.id || row?._id ? 1 : 0.5,
                    pointerEvents: row?.id || row?._id ? "auto" : "none",
                  }}
                >
                  View
                </span>
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
